import { LoadingOutlined } from '@ant-design/icons';
import AnimatingDots from '~/components/AnimatingDots';
import Button from '~/components/Button';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import StyledInput from '~/components/StyledInput';
import Text from '~/components/Text';
import {
  COUNTRIES,
  COUNTRIES_CODES,
  getCountryCodeByCountryName,
  getCurrencyByCountry,
} from '~/constants/address';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { CURRENCY_LIST } from '~/utils/amount';
import * as Styles from './styles';
import { useAddClient } from './useAddClient';

interface IAddClientProps {
  businessClientUuid: string | null;
  onClose(): void;
  onClientCreated?: (client: any) => void;
}

const AddClient = ({ businessClientUuid, onClose, onClientCreated }: IAddClientProps) => {
  const isDesktop = useIsDesktop();
  const { client, isLoading, isSaving, onChange, saveClient } = useAddClient(
    businessClientUuid,
    onClose,
    onClientCreated,
  );

  if (isLoading)
    return (
      <Styles.Wrapper className="full-width center">
        <AnimatingDots />
      </Styles.Wrapper>
    );

  return (
    <Styles.Wrapper id="clientFormContainer">
      <Styles.ContentWrapper>
        <Text size={isDesktop ? 'smd' : 'ssmd'}>Basic information</Text>
        <Gap t={isDesktop ? 24 : 16} />

        <div className="center">
          <div className="full-flex">
            <Text size="xs">
              First Name <span className="danger">*</span>
            </Text>
            <Gap t={4} />
            <StyledInput
              className={`${!client.first_name && 'error-field'}`}
              value={client.first_name}
              onChange={e => onChange({ first_name: e.target.value })}
            />
          </div>
          <Gap r={16} />
          <div className="full-flex">
            <Text size="xs">Last Name</Text>
            <Gap t={4} />
            <StyledInput
              value={client.last_name}
              onChange={e => onChange({ last_name: e.target.value })}
            />
          </div>
        </div>

        <Gap t={16} />
        <Text size="xs">
          Company Name <span className="danger">*</span>
        </Text>
        <Gap t={4} />
        <StyledInput
          className={`${!client.company_name && 'error-field'}`}
          value={client.company_name}
          onChange={e => onChange({ company_name: e.target.value })}
        />

        <Gap t={16} />
        <div className="center">
          <div className="full-flex" style={{ width: '50%', marginRight: '16px' }}>
            <Text size="xs">
              Country <span className="danger">*</span>
            </Text>
            <Gap t={4} />
            <DropdownInput
              search
              className={`${!client.country && 'error-field'}`}
              value={client.country || ''}
              onChange={value => {
                onChange({ country: value });
                if (!client.currency) {
                  const countryCurrency = getCurrencyByCountry(value);
                  onChange({ currency: countryCurrency });
                }
                const countryCode = getCountryCodeByCountryName(value);
                if (countryCode) {
                  onChange({ country_code: countryCode });
                }
              }}
              options={COUNTRIES}
            />
          </div>

          <div className="full-flex" style={{ width: 'calc(50% - 16px)' }}>
            <Text size="xs">
              Currency <span className="danger">*</span>
            </Text>
            <Gap t={4} />
            <DropdownInput
              className={`${!client.currency && 'error-field'}`}
              value={client.currency}
              onChange={value => onChange({ currency: value })}
              options={CURRENCY_LIST}
            />
          </div>
        </div>

        <Gap t={32} />
        <Text size={isDesktop ? 'smd' : 'ssmd'}>Additional information</Text>
        <Gap t={isDesktop ? 24 : 16} />

        <Gap t={16} />
        <Text size="xs">Address Line 1</Text>
        <Gap t={4} />
        <StyledInput
          value={client.address_line_1 || ''}
          onChange={e => onChange({ address_line_1: e.target.value })}
        />

        <Gap t={16} />
        <div className="center">
          <div className="full-flex">
            <Text size="xs">Address Line 2</Text>
            <Gap t={4} />
            <StyledInput
              value={client.address_line_2 || ''}
              onChange={e => onChange({ address_line_2: e.target.value })}
            />
          </div>
          <Gap r={16} />
          <div className="full-flex">
            <Text size="xs">City</Text>
            <Gap t={4} />
            <StyledInput
              value={client.city || ''}
              onChange={e => onChange({ city: e.target.value })}
            />
          </div>
        </div>

        <Gap t={16} />
        <div className="center">
          <div className="full-flex">
            <Text size="xs">State / Province</Text>
            <Gap t={4} />
            <StyledInput
              value={client.state || ''}
              onChange={e => onChange({ state: e.target.value })}
            />
          </div>
          <Gap r={16} />
          <div className="full-flex">
            <Text size="xs">Postal code</Text>
            <Gap t={4} />
            <StyledInput
              value={client.postal_code || ''}
              onChange={e => onChange({ postal_code: e.target.value })}
            />
          </div>
        </div>

        <Gap t={16} />
        <div className="center">
          <div className="full-flex" style={{ width: '50%' }}>
            <Text size="xs">Country Code</Text>
            <Gap t={4} />
            <DropdownInput
              search
              value={client.country_code || ''}
              onChange={value => {
                onChange({ country_code: value });
              }}
              options={COUNTRIES_CODES}
              showSelection={false}
            />
          </div>
          <Gap r={16} />
          <div className="full-flex" style={{ width: '50%' }}>
            <Text size="xs">Phone</Text>
            <Gap t={4} />
            <StyledInput
              value={client.phone || ''}
              onChange={e => onChange({ phone: e.target.value })}
            />
          </div>
        </div>

        <Gap t={16} />
        <Text size="xs">Email</Text>
        <Gap t={4} />
        <StyledInput
          value={client.email}
          onChange={e => onChange({ email: e.target.value })}
          placeholder="e.g. John.mayer@gmail.com"
          name="email"
        />

        <Gap t={16} />
        <div className="center">
          <div className="full-flex" style={{ width: '50%' }}>
            <Text size="xs">Website URL</Text>
            <Gap t={4} />
            <StyledInput
              value={client.website || ''}
              onChange={e => onChange({ website: e.target.value })}
            />
          </div>
        </div>

        <Gap t={86} />
      </Styles.ContentWrapper>

      <Styles.Footer>
        <Button
          size={isDesktop ? 'md' : 'sm'}
          className="full-width"
          disabled={isSaving}
          onClick={saveClient}
        >
          {isSaving ? <LoadingOutlined /> : 'Save'}
        </Button>
      </Styles.Footer>
    </Styles.Wrapper>
  );
};

export default AddClient;
